import { useEffect, useState } from 'react';

import { BackButton } from '@/components/BackButton';
import { Loader } from '@/components/Loader';
import { useFreeSlots } from '@/hooks/useFreeSlots';
import { AppointmentTypeSelector } from '@/modules/AppointmentTypeSelector';
import { DoctorFreeSlots } from '@/modules/DoctorFreeSlots';
import { DoctorSelector } from '@/modules/DoctorSelector';
import { useBookingStore } from '@/store/useBookingStore';
import { usePracticeStore } from '@/store/usePracticeStore';

import styles from './BookingApp.module.css';

export const BookingApp = ({ callback }: { callback?: () => void }) => {
  const { isFetching: isFetchingFreeSlots, freeSlots } = useFreeSlots();
  const { provider, appointmentType, setAppointmentType, clearSelection } = useBookingStore();
  const { providers, connection } = usePracticeStore();
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showAppointmentTypes, setShowAppointmentTypes] = useState(false);
  const [showDoctorSelector, setShowDoctorSelector] = useState(false);

  useEffect(() => {
    setShowTimePicker(!isFetchingFreeSlots && !!freeSlots);
  }, [isFetchingFreeSlots, freeSlots]);

  useEffect(() => {
    if (connection) {
      setShowAppointmentTypes(!!connection.displayAppointmentTypeFirst);
      setShowDoctorSelector(!connection.displayAppointmentTypeFirst);
    }
    if (!provider && appointmentType) {
      setShowDoctorSelector(true);
      setShowAppointmentTypes(false);
    } else if (!appointmentType && provider) {
      setShowAppointmentTypes(true);
      setShowDoctorSelector(false);
    }
  }, [provider, appointmentType, connection]);

  const hasProviders = providers.length > 0;

  if (isFetchingFreeSlots) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (!hasProviders) {
    return <p className={styles.noResults}>No results found</p>;
  }

  const hideAppointmentTypes = () => {
    setShowAppointmentTypes(false);
    clearSelection();
  };

  const hideDoctorSelector = () => {
    setShowDoctorSelector(false);
    clearSelection();
  };

  const closeSlots = () => {
    setShowTimePicker(false);
    if (Number(provider?.appointment_types?.length) < 2) {
      clearSelection();
    } else {
      setAppointmentType(null, []);
    }
  };

  const handleBack = () => {
    if (provider && showAppointmentTypes) {
      hideAppointmentTypes();
    } else if (appointmentType && !provider) {
      hideDoctorSelector();
    } else if (showTimePicker) {
      closeSlots();
    } else {
      clearSelection();
    }
  };

  const displayBackButton = () => {
    if (connection?.skipProviderSelection && !appointmentType) {
      return null;
    }
    if (provider || appointmentType) {
      return (
        <div className={styles.backBtnContainer}>
          <BackButton onClick={handleBack} />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {showTimePicker ? (
        <section className={styles.datetimeSelectorContainer}>
          <DoctorFreeSlots goBack={closeSlots} callback={callback} />
        </section>
      ) : (
        <section className={styles.container}>
          {showDoctorSelector && <DoctorSelector />}
          {showAppointmentTypes && <AppointmentTypeSelector />}
          {displayBackButton()}
        </section>
      )}
    </>
  );
};
